import React, { useState, useEffect } from 'react';
import { BookOpen, Save, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import Header from '../components/Header';

const JOURNAL_KEY = 'recovery_journal';

const JournalScreen = () => {
  const [entries, setEntries] = useState({});
  const [todayText, setTodayText] = useState('');
  const [saved, setSaved] = useState(false);
  const [expandedDates, setExpandedDates] = useState({});

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(JOURNAL_KEY) || '{}');
      setEntries(stored);
      setTodayText(stored[today]?.text || '');
    } catch {}
  }, []);

  const handleSave = () => {
    const updated = {
      ...entries,
      [today]: { text: todayText, timestamp: new Date().toISOString() }
    };
    setEntries(updated);
    localStorage.setItem(JOURNAL_KEY, JSON.stringify(updated));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleDelete = (date) => {
    const updated = { ...entries };
    delete updated[date];
    setEntries(updated);
    localStorage.setItem(JOURNAL_KEY, JSON.stringify(updated));
  };

  const toggleExpand = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const sortedDates = Object.keys(entries).sort((a, b) => b.localeCompare(a));

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('he-IL', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  return (
    <div dir="rtl" className="min-h-screen bg-stone-50 pb-24">
      <Header title="יומן אישי" subtitle="מחשבות חופשיות" />

      <div className="px-4 py-4 space-y-4">
        {/* Today's Entry */}
        <div className="bg-white rounded-xl p-4 shadow">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-brand-500" />
            <h3 className="font-bold text-stone-800">היום - {formatDate(today)}</h3>
          </div>
          <textarea
            value={todayText}
            onChange={e => setTodayText(e.target.value)}
            placeholder="איך היה היום? מה הרגשת? מה עזר לך? כתוב בחופשיות..."
            className="w-full px-3 py-3 border border-stone-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-400 min-h-36"
            rows={6}
          />
          <div className="flex justify-between items-center mt-3">
            <span className="text-xs text-stone-400">{todayText.length} תווים</span>
            <button
              onClick={handleSave}
              disabled={!todayText.trim()}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition ${
                saved ? 'bg-brand-500 text-white' :
                todayText.trim() ? 'bg-brand-500 text-white hover:bg-brand-600' :
                'bg-stone-200 text-stone-400 cursor-not-allowed'
              }`}
            >
              <Save className="w-4 h-4" />
              {saved ? 'נשמר! ✅' : 'שמור'}
            </button>
          </div>
        </div>

        {/* Past Entries */}
        {sortedDates.filter(d => d !== today).length > 0 && (
          <div>
            <h3 className="font-bold text-stone-800 mb-3">📚 רשומות קודמות</h3>
            <div className="space-y-2">
              {sortedDates.filter(d => d !== today).map(date => (
                <div key={date} className="bg-white rounded-xl shadow overflow-hidden">
                  <button
                    onClick={() => toggleExpand(date)}
                    className="w-full flex items-center justify-between p-4"
                  >
                    <span className="font-semibold text-stone-700 text-sm">{formatDate(date)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-stone-400">
                        {entries[date].text.slice(0, 20)}...
                      </span>
                      {expandedDates[date] ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
                    </div>
                  </button>
                  {expandedDates[date] && (
                    <div className="px-4 pb-4 border-t border-stone-100">
                      <p className="text-sm text-stone-700 mt-3 whitespace-pre-wrap leading-relaxed">
                        {entries[date].text}
                      </p>
                      <button
                        onClick={() => handleDelete(date)}
                        className="mt-3 flex items-center gap-1 text-xs text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-3 h-3" /> מחק רשומה
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {sortedDates.length === 0 && (
          <div className="text-center py-12 text-stone-400">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-semibold">עדיין אין רשומות</p>
            <p className="text-sm mt-1">כתוב את המחשבה הראשונה שלך ↑</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JournalScreen;
